import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * A PDF, read INSIDE the app (T731).
 *
 * App-wide primitive, not the player's: any surface that today hands a member a file to
 * download (the public guides, the pregnancy guide, the auto-test bonus) can mount this
 * instead and keep her where she is. Adopting it is each surface's own decision — a lead
 * magnet's download button is a conversion mechanic, not a rendering choice — so this file
 * only makes the reader available.
 *
 * Why this exists rather than an `<iframe>` or a link out. An `<iframe>` pointing at a PDF
 * stays blank on Safari for iOS and Chrome for Android — that was the reported defect
 * (`Los PDFs en la app no se ven`, 2026-09-13) — and `object-src 'none'` closes
 * `<object>` / `<embed>`. The obvious replacement, a top-level navigation to the file,
 * works on every device but breaks the installed app: the manifest declares
 * `display: standalone`, so `target="_blank"` throws the member out of the shell into
 * Safari's own chrome, with no way back that belongs to us (founder, 2026-09-13). Painting
 * the pages ourselves is the only path that is both readable on a phone and inside the app.
 *
 * Its own strings live in the `common` namespace for the same reason the component lives in
 * `ui/`: a viewer that reads `player` keys is not reusable, it is the player's with a new path.
 *
 * The load is deliberately lazy and client-only: `pdfjs-dist` is imported inside the effect,
 * so it is a separate chunk that a member who never opens a document never downloads, and it
 * never reaches the Workers SSR bundle.
 *
 * It is the LEGACY build on purpose. The modern one calls `Map.prototype.getOrInsertComputed`,
 * a proposal method this container's Chromium does not have — page 1 painted and page 2 died
 * with `TypeError: …getOrInsertComputed is not a function` (measured 2026-09-13). Whatever the
 * exact version line is, a member on a phone she has not updated in two years is the reader we
 * are building for, and the legacy bundle carries its own polyfills for exactly that.
 *
 * CSP notes, all satisfied without touching the policy:
 *   · the worker is a same-origin asset (Vite `?url`), so `script-src 'self'` covers it —
 *     a CDN `workerSrc` would be refused;
 *   · pdf.js 6 dropped its `eval` path altogether, so the policy's missing `'unsafe-eval'`
 *     is not a problem — there is no option left to turn off;
 *   · the document itself is fetched from our own `/media/` gate, under `connect-src 'self'`.
 *
 * The document is fetched WHOLE (`disableRange` + `disableStream`). A phase ficha is a few
 * hundred KB, so progressive range fetching buys nothing and costs a divergence: pdf.js's
 * partial requests drive `R2Bucket.get(key, { range })`, which throws `DevalueError` under
 * Vite dev + Miniflare (the same class of dev-only breakage `media-r2-private-stream.server.ts`
 * already documents for the client Headers object) and answers `503` from the media gate.
 * One plain `200` is the path both the local rung and production actually exercise.
 *
 * Zoom is the browser's, not ours: `app/root.tsx` ships
 * `width=device-width, initial-scale=1` with no `user-scalable=no`, so pinch works on the
 * rendered pages. Each page is rasterised at the container width times the device pixel
 * ratio (capped at 2) so it stays sharp under that zoom without rendering a page nobody
 * reads at four times the memory.
 */

/** Upper bound on the rasterised width, in CSS px — a page wider than this reads worse, not better. */
const MAX_PAGE_CSS_WIDTH = 900;
/** Above 2 the memory cost grows faster than anything the eye gets back. */
const MAX_PIXEL_RATIO = 2;
/**
 * How long the skeleton may stand before it becomes the error state.
 *
 * Without this there is no bound at all: a request that hangs rather than fails leaves a
 * member looking at a grey rectangle forever, with the error path never reached and so the
 * way out never offered. The comprehension judge found it from the outside, reading the
 * loading state and asking the question the screen could not answer — "si no llega a
 * abrirse no me dice cuánto tarda, ni cómo salgo de aquí". 30 s is long enough for a phase
 * document on a bad connection and short enough that nobody decides the app is broken.
 */
const LOAD_TIMEOUT_MS = 30_000;

/** What the viewer is doing, for a surface that has to place its own affordances around it. */
export type PdfViewerStatus = "loading" | "ready" | "error";

// Not exported: nothing consumes the type yet, and the knip ratchet sits at zero —
// a dead export is debt whatever its file. Export it the day a caller needs it.
interface PdfViewerProps {
  /** Same-origin `/media/…` URL of the document. */
  url: string;
  /** The document's member-facing title, for the per-page accessible names. */
  label: string;
  /**
   * Told every time the viewer changes state. The mounting surface needs it because a
   * failure inverts the weight of whatever it puts around the reader: a link that is
   * correctly subordinate beside a document that painted is the ONLY way out when the
   * document did not (design rubric, Criterio 2, this ticket's round-1 review).
   */
  onStatusChange?: (status: PdfViewerStatus) => void;
}

export function PdfViewer({ url, label, onStatusChange }: PdfViewerProps) {
  const { t } = useTranslation("common");
  // Held in a ref so it can stay OUT of the effect's deps. react-i18next swaps `t`'s identity
  // when a namespace finishes loading or the language changes, and with `t` in the array that
  // tore the worker down and re-fetched and re-rasterised the entire document — for a string
  // used only in a per-page `aria-label` (AI review round 2).
  const tRef = useRef(t);
  tRef.current = t;
  const hostRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<PdfViewerStatus>("loading");
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Every teardown the effect owes on unmount: the worker-backed document, and any page
    // render still in flight. Without them a fast open→close leaks a worker per open.
    let destroy: (() => void) | null = null;

    // Bounded, not hopeful: a hung fetch never rejects, so only a clock turns the skeleton
    // into a state that offers her a way out.
    let timedOut = false;
    const timeout = setTimeout(() => {
      if (cancelled) return;
      // Ending the load, not relabelling it: without `timedOut` a document that arrives at
      // minute three would paint its pages over the error copy she is already reading, and
      // without `destroy()` its worker would outlive the state that gave up on it
      // (AI review round 4).
      timedOut = true;
      destroy?.();
      destroy = null;
      setStatus((current) => (current === "loading" ? "error" : current));
    }, LOAD_TIMEOUT_MS);

    async function render() {
      const host = hostRef.current;
      if (!host) return;
      try {
        const [pdfjs, workerUrl] = await Promise.all([
          import("pdfjs-dist/legacy/build/pdf.mjs"),
          import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url").then((m) => m.default),
        ]);
        if (cancelled || timedOut) return;
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

        // `destroy()` lives on the loading task, not on the document (pdf.js 6): it is what
        // tears the worker down, so it is the handle the cleanup has to keep.
        const task = pdfjs.getDocument({ url, disableRange: true, disableStream: true });
        const doc = await task.promise;
        if (cancelled || timedOut) {
          void task.destroy();
          return;
        }
        destroy = () => void task.destroy();
        setPageCount(doc.numPages);

        // Measured on the WRAPPER, never on `host`: the pages container carries `hidden`
        // until the first page paints, and a hidden element reports `clientWidth: 0` — so
        // this fell through to the 900 px cap on every device and rasterised a 390 px phone
        // at 1800 device px, four times the pixels it can show, on exactly the hardware this
        // ticket exists for (AI review round 2).
        const measured = host.parentElement?.clientWidth || host.clientWidth;
        const cssWidth = Math.min(measured || MAX_PAGE_CSS_WIDTH, MAX_PAGE_CSS_WIDTH);
        const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);

        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n);
          if (cancelled || timedOut) return;
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (cssWidth / base.width) * ratio });

          // The delivered reader marks each sheet with its position
          // (`design_handoff_tfc/player/components.jsx:421`, `Página 1 · de {item.pages}`).
          // It matters more here than in the mock: a rasterised page is taller than a phone
          // screen, so the sheet edges are never both in view and nothing else says how long
          // the document is. Only from two pages up — on a one-page ficha it is chrome with
          // nothing to orient.
          if (doc.numPages > 1) {
            const mark = document.createElement("p");
            mark.className = "tfc-pdf-page-mark";
            mark.textContent = tRef.current("pdf.page_mark", { page: n, total: doc.numPages });
            host.appendChild(mark);
          }

          const canvas = document.createElement("canvas");
          canvas.className = "tfc-pdf-canvas";
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = "100%";
          canvas.setAttribute("role", "img");
          canvas.setAttribute("aria-label", tRef.current("pdf.page_aria", { title: label, page: n, total: doc.numPages }));
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("2d context unavailable");
          host.appendChild(canvas);
          await page.render({ canvas, canvasContext: ctx, viewport }).promise;
          if (cancelled || timedOut) return;
          // First page painted is the moment the skeleton has to go: waiting for the last
          // one leaves a three-page document looking broken while page 3 rasterises.
          if (n === 1) setStatus("ready");
        }
        setStatus("ready");
      } catch (e) {
        if (cancelled || timedOut) return;
        console.error("[pdf-viewer] could not render the document", e);
        setStatus("error");
      }
    }

    void render();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      destroy?.();
      if (hostRef.current) hostRef.current.replaceChildren();
    };
  }, [url, label]);

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  return (
    <div className="tfc-pdf-viewer">
      {status === "loading" ? (
        <div className="tfc-pdf-skeleton" role="status" aria-live="polite">
          <span className="tfc-skel-card" aria-hidden="true" />
          <span className="tfc-pdf-status">{t("pdf.loading")}</span>
        </div>
      ) : null}
      {status === "error" ? (
        <p className="tfc-pdf-status is-error" role="status">{t("pdf.error")}</p>
      ) : null}
      <div
        ref={hostRef}
        className="tfc-pdf-pages"
        data-page-count={pageCount || undefined}
        hidden={status !== "ready"}
      />
    </div>
  );
}
